import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined,
  FilePdfOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LinkOutlined,
  LoadingOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Empty, Flex, Modal, Segmented, Switch, Tag, Tooltip, Typography } from "antd"
import dayjsNow, { type Dayjs } from "dayjs"
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { type TFunction, useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../../../../components/ActionTooltip.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import {
  barLabelSide,
  barStatusFor,
  buildGanttLayout,
  connectorEmphasis,
  connectorsInRows,
  todayBadgeAlign,
  type RowWindow,
  visibleRowWindow,
  type ScheduledRow,
  totalDaysOf
} from "./gantt-layout.ts"
import {
  anchoredScrollLeft,
  axisUnitFor,
  bandUnitFor,
  buildAxisTicks,
  defaultZoomFor,
  formatWeekRange,
  type GanttRange,
  type GanttZoom,
  MAX_DAY_WIDTH,
  MIN_DAY_WIDTH,
  resolveDayWidth,
  tickDisplayLabel,
  wheelZoom,
  ZOOM_LEVELS,
  zoomLevelFor
} from "./gantt-scale.ts"
import { type GanttRowItem, ganttRows } from "./gantt-rows.ts"
import { ganttTimelineRange } from "./gantt-timeline-range.ts"
import { planGanttPdfPages } from "./GanttPdfExport/gantt-pdf-plan.ts"
import { useGanttPdfExport } from "./GanttPdfExport/use-gantt-pdf-export.tsx"
import { MilestoneStatusTag } from "./MilestoneStatusTag.tsx"
import { useGanttZoom } from "./use-gantt-zoom.ts"
import {
  MILESTONE_STATUS_ORDER,
  MILESTONE_STATUSES_MAP
} from "../../../../constants/milestone-statuses.ts"
import { TYPE_INFO, lagPhraseShort } from "../../../../constants/milestone-dependency-relationship.ts"
import { getMilestoneColor } from "../../../../constants/milestone-palette.ts"
import {
  calculateEffectiveSchedule,
  type EffectiveScheduleEntry
} from "../../../../utils/milestone-effective-schedule.ts"
import { isUnscheduledMilestone } from "../../../../utils/milestone-effort.ts"
import { type MilestoneDependency } from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"

const { Text } = Typography

interface GanttSizes {
  /** The widest a day may be drawn on Fit, so a two-day project is not
   * stretched into columns hundreds of pixels wide. */
  maxDayWidth: number
  rowHeight: number
  headerHeight: number
  bandHeaderHeight: number
  /** Its own row between the band header and the tick labels, so the Today
   * badge never shares vertical space with a date label it could cover. */
  todayRowHeight: number
  barHeight: number
  stub: number
  milestoneColWidth: number
  dependenciesColWidth: number
  statusColWidth: number
  /** Cap on the chart's own scroll box; it scrolls inside rather than
   * growing the page. */
  maxViewportHeight: string
}

// The expanded workspace isn't the compact chart scaled up — it gets a wider
// day-width ceiling, taller rows and more room for names, so the same data
// reads with more room rather than just being blown up.
const COMPACT_SIZES: GanttSizes = {
  maxDayWidth: 60,
  rowHeight: 44,
  headerHeight: 26,
  bandHeaderHeight: 24,
  todayRowHeight: 20,
  barHeight: 26,
  stub: 12,
  milestoneColWidth: 170,
  dependenciesColWidth: 95,
  statusColWidth: 88,
  maxViewportHeight: "70vh"
}

const EXPANDED_SIZES: GanttSizes = {
  maxDayWidth: 90,
  rowHeight: 52,
  headerHeight: 28,
  bandHeaderHeight: 26,
  todayRowHeight: 22,
  barHeight: 32,
  stub: 16,
  milestoneColWidth: 230,
  dependenciesColWidth: 120,
  statusColWidth: 110,
  maxViewportHeight: "calc(88vh - 190px)"
}

// Until the chart has been measured (and in jsdom, where it never is).
const UNMEASURED_CHART_WIDTH = 720

const TEAL = "var(--ant-color-primary)"
const OVERDUE_RED = "var(--ant-color-error)"

interface ProjectViewMilestonesGanttProps {
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  projectName?: string
  /** The zoom is remembered per user against this project. */
  projectId?: string
}

interface RelatedDependencyRow {
  dependency: MilestoneDependency
  otherName: string
  relation: string
}

// Only the separator lives here; the wording (and its plural) is the shared
// translated one.
const lagSuffix = (t: TFunction, days: number) => {
  const phrase = lagPhraseShort(t, days)
  return phrase ? ` · ${phrase}` : ""
}

export function ProjectViewMilestonesGantt({
  milestones,
  dependencies,
  projectName,
  projectId
}: ProjectViewMilestonesGanttProps) {
  const { parseDate } = useDateFormat()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [zoom, setZoom] = useGanttZoom(projectId)
  const [showConnectors, setShowConnectors] = useState(true)
  const [grouped, setGrouped] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const compactPdfExport = useGanttPdfExport({ projectName })
  const expandedPdfExport = useGanttPdfExport({ projectName })
  const { t } = useTranslation()

  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    milestones.forEach((m) => map.set(m.id, m.name))
    return map
  }, [milestones])

  const colorById = useMemo(() => {
    const map = new Map<string, string>()
    milestones.forEach((m, index) => map.set(m.id, getMilestoneColor(index)))
    return map
  }, [milestones])

  const dependencyCountByMilestone = useMemo(() => {
    const map = new Map<string, number>()
    dependencies.forEach((d) => {
      map.set(d.source_milestone, (map.get(d.source_milestone) || 0) + 1)
      map.set(d.target_milestone, (map.get(d.target_milestone) || 0) + 1)
    })
    return map
  }, [dependencies])

  const directDependenciesFor = useCallback(
    (milestoneId: string): RelatedDependencyRow[] =>
      dependencies
        .filter((d) => d.source_milestone === milestoneId || d.target_milestone === milestoneId)
        .map((d) => {
          const isSource = d.source_milestone === milestoneId
          return {
            dependency: d,
            otherName: nameById.get(isSource ? d.target_milestone : d.source_milestone) || "—",
            relation: isSource ? "Required for" : "Depends on"
          }
        }),
    [dependencies, nameById]
  )

  // The single scheduling engine: a milestone's dates here are never its raw
  // Planned Start/End if a dependency requires later ones — the Table reads
  // from the exact same function, so the two views can never disagree. This
  // one calculation is shared by both the inline and expanded surfaces below.
  const effectiveSchedule = useMemo(
    () => calculateEffectiveSchedule({ milestones, dependencies, parseDate }),
    [milestones, dependencies, parseDate]
  )

  const { rows, hasSections } = useMemo(
    () => ganttRows(milestones, { grouped, collapsed }),
    [milestones, grouped, collapsed]
  )

  const scheduled = useMemo<ScheduledRow[]>(
    () =>
      rows
        .map(({ milestone }, rowIndex) => {
          const entry = effectiveSchedule.get(milestone.id)
          return entry ? { milestone, rowIndex, entry } : null
        })
        .filter((row): row is ScheduledRow => !!row),
    [rows, effectiveSchedule]
  )

  // From every milestone, not only the rows shown: collapsing a section
  // must not move the axis under the user.
  const range = useMemo(() => {
    const entries = milestones.flatMap((m) => {
      const entry = effectiveSchedule.get(m.id)
      return entry ? [entry] : []
    })
    if (!entries.length) return null
    const starts = entries.map((entry) => entry.start)
    // An actual finish can land past the plan, and its marker is positioned
    // against this range — left out, it paints beyond the last header cell.
    const ends = entries.flatMap((entry) =>
      entry.actualEnd ? [entry.end, entry.actualEnd] : [entry.end]
    )
    return ganttTimelineRange(starts, ends, dayjsNow().startOf("day"))
  }, [milestones, effectiveSchedule])

  const toggleSection = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Everything reachable from the selected milestone through the dependency
  // graph, in either direction — the "chain" to highlight, not just its
  // immediate neighbours. Shared by both surfaces since selection is one
  // piece of state on this component, not duplicated per surface.
  const connected = useMemo(() => {
    if (!selectedId) return null
    const adjacency = new Map<string, string[]>()
    dependencies.forEach((dep) => {
      adjacency.set(dep.source_milestone, [
        ...(adjacency.get(dep.source_milestone) || []),
        dep.target_milestone
      ])
      adjacency.set(dep.target_milestone, [
        ...(adjacency.get(dep.target_milestone) || []),
        dep.source_milestone
      ])
    })
    const visited = new Set<string>([selectedId])
    const queue = [selectedId]
    while (queue.length) {
      const current = queue.shift() as string
      for (const next of adjacency.get(current) || []) {
        if (!visited.has(next)) {
          visited.add(next)
          queue.push(next)
        }
      }
    }
    return visited
  }, [selectedId, dependencies])

  const clearSelection = () => setSelectedId(null)
  const toggleSelect = (id: string, e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    setSelectedId((current) => (current === id ? null : id))
  }

  const selectedMilestone = selectedId
    ? milestones.find((m) => m.id === selectedId) || null
    : null

  if (!range) {
    return (
      <div className="py-10">
        <Empty description="Set Planned Start and Planned End dates on your milestones to see them on the timeline." />
      </div>
    )
  }

  const sharedProps = {
    rows,
    hasSections,
    grouped,
    onGroupedChange: setGrouped,
    onToggleSection: toggleSection,
    scheduled,
    range,
    dependencies,
    connected,
    selectedId,
    colorById,
    dependencyCountByMilestone,
    directDependenciesFor,
    nameById,
    parseDate,
    zoom,
    onZoomChange: setZoom,
    showConnectors,
    onShowConnectorsChange: setShowConnectors,
    onToggleSelect: toggleSelect,
    onClearSelection: clearSelection
  }

  return (
    <div>
      <div className="flex items-center justify-end gap-3 mb-1.5">
        <button
          type="button"
          onClick={compactPdfExport.generate}
          disabled={compactPdfExport.generating}
          aria-label={t("milestonesGanttPdf.generateButton")}
          className="flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors"
          style={{
            color: TEAL,
            background: "transparent",
            opacity: compactPdfExport.generating ? 0.6 : 1
          }}
        >
          {compactPdfExport.generating ? (
            <LoadingOutlined style={{ fontSize: 12 }} spin />
          ) : (
            <FilePdfOutlined style={{ fontSize: 12 }} />
          )}
          {t("milestonesGanttPdf.generateButton")}
        </button>
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors"
          style={{ color: TEAL, background: "transparent" }}
        >
          <FullscreenOutlined style={{ fontSize: 12 }} />
          Expand Gantt
        </button>
      </div>

      <GanttChartBody
        {...sharedProps}
        sizes={COMPACT_SIZES}
      />
      {compactPdfExport.printing && (
        <GanttPrintSurface printRef={compactPdfExport.printRef} bodyProps={sharedProps} />
      )}
      {compactPdfExport.modal}
      <MilestonesGanttLegend />
      {selectedMilestone && (
        <MilestoneDetailPanel
          milestone={selectedMilestone}
          related={directDependenciesFor(selectedMilestone.id)}
          effectiveEntry={effectiveSchedule.get(selectedMilestone.id) || null}
          nameById={nameById}
          parseDate={parseDate}
        />
      )}

      {isExpanded && (
        <Modal
          open
          onCancel={() => setIsExpanded(false)}
          footer={null}
          width="92vw"
          style={{ top: "4vh" }}
          styles={{ body: { maxHeight: "88vh", overflowY: "auto" } }}
          title={
            <div className="flex items-center justify-between pr-6">
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Gantt Schedule</div>
                {projectName && (
                  <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                    {projectName}
                  </Text>
                )}
              </div>
              <Flex gap="small" align="center">
                <button
                  type="button"
                  onClick={expandedPdfExport.generate}
                  disabled={expandedPdfExport.generating}
                  aria-label={t("milestonesGanttPdf.generateButton")}
                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded"
                  style={{ color: TEAL, opacity: expandedPdfExport.generating ? 0.6 : 1 }}
                >
                  {expandedPdfExport.generating ? (
                    <LoadingOutlined style={{ fontSize: 12 }} spin />
                  ) : (
                    <FilePdfOutlined style={{ fontSize: 12 }} />
                  )}
                  {t("milestonesGanttPdf.generateButton")}
                </button>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded"
                  style={{ color: TEAL }}
                >
                  <FullscreenExitOutlined style={{ fontSize: 12 }} />
                  Collapse
                </button>
              </Flex>
            </div>
          }
        >
          <GanttChartBody
            {...sharedProps}
            sizes={EXPANDED_SIZES}
          />
          {expandedPdfExport.printing && (
            <GanttPrintSurface printRef={expandedPdfExport.printRef} bodyProps={sharedProps} />
          )}
          {expandedPdfExport.modal}
          <MilestonesGanttLegend />
          {selectedMilestone && (
            <MilestoneDetailPanel
              milestone={selectedMilestone}
              related={directDependenciesFor(selectedMilestone.id)}
              effectiveEntry={effectiveSchedule.get(selectedMilestone.id) || null}
              nameById={nameById}
              parseDate={parseDate}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

type ChartBodyProps = React.ComponentProps<typeof GanttChartBody>

/**
 * Off-screen pages for the PDF: the whole project on the Fit scale the PDF
 * plan picks, every row, the same chart component the screen uses.
 */
function GanttPrintSurface({
  printRef,
  bodyProps
}: {
  printRef: React.RefObject<HTMLDivElement | null>
  bodyProps: Omit<ChartBodyProps, "sizes" | "print">
}) {
  const sizes = COMPACT_SIZES
  const plan = planGanttPdfPages({
    totalDays: totalDaysOf(bodyProps.range),
    rowCount: bodyProps.rows.length,
    leftPanelWidth: sizes.milestoneColWidth + sizes.dependenciesColWidth + sizes.statusColWidth,
    headerHeight: sizes.bandHeaderHeight + sizes.todayRowHeight + sizes.headerHeight,
    rowHeight: sizes.rowHeight,
    maxDayWidth: sizes.maxDayWidth
  })
  return (
    <div
      ref={printRef}
      aria-hidden
      data-testid="gantt-print-surface"
      style={{ position: "fixed", left: -100000, top: 0, pointerEvents: "none" }}
    >
      {plan.pages.map((page) => (
        <div key={page.first} data-pdf-page="">
          <GanttChartBody
            {...bodyProps}
            connected={null}
            selectedId={null}
            sizes={sizes}
            print={{ dayWidth: plan.dayWidth, rows: page }}
          />
        </div>
      ))}
    </div>
  )
}

function GanttZoomControl({
  value,
  onChange
}: {
  value: GanttZoom
  onChange: (zoom: GanttZoom) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Segmented
        size="small"
        aria-label={t("milestonesGantt.zoom.label")}
        value={zoomLevelFor(value) ?? ""}
        onChange={(level) => onChange(level as GanttZoom)}
        options={ZOOM_LEVELS.map((level) => ({
          value: level,
          label: t(`milestonesGantt.zoom.${level}`)
        }))}
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        {t("milestonesGantt.zoom.hint")}
      </Text>
    </div>
  )
}

function GanttChartBody({
  rows,
  hasSections,
  grouped,
  onGroupedChange,
  onToggleSection,
  scheduled,
  range,
  dependencies,
  sizes,
  connected,
  selectedId,
  colorById,
  dependencyCountByMilestone,
  directDependenciesFor,
  nameById,
  parseDate,
  zoom,
  onZoomChange,
  showConnectors,
  onShowConnectorsChange,
  onToggleSelect,
  onClearSelection,
  print
}: {
  rows: GanttRowItem[]
  hasSections: boolean
  grouped: boolean
  onGroupedChange: (grouped: boolean) => void
  onToggleSection: (id: string) => void
  scheduled: ScheduledRow[]
  range: GanttRange
  dependencies: MilestoneDependency[]
  sizes: GanttSizes
  connected: Set<string> | null
  selectedId: string | null
  colorById: Map<string, string>
  dependencyCountByMilestone: Map<string, number>
  directDependenciesFor: (id: string) => RelatedDependencyRow[]
  nameById: Map<string, string>
  parseDate: (value: unknown) => Dayjs | null
  zoom: GanttZoom | null
  onZoomChange: (zoom: GanttZoom) => void
  showConnectors: boolean
  onShowConnectorsChange: (show: boolean) => void
  onToggleSelect: (id: string, e?: React.SyntheticEvent) => void
  onClearSelection: () => void
  /** Drawn for the PDF: a fixed scale, one page of rows, no controls. */
  print?: { dayWidth: number; rows: RowWindow }
}) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [scroll, setScroll] = useState({ top: 0, left: 0 })
  const today = useMemo(() => dayjsNow().startOf("day"), [])
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (rect?.width) setViewportWidth(rect.width)
      if (rect?.height) setViewportHeight(rect.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const leftPanelWidth = sizes.milestoneColWidth + sizes.dependenciesColWidth + sizes.statusColWidth
  const headerHeight = sizes.bandHeaderHeight + sizes.todayRowHeight + sizes.headerHeight
  const bodyHeight = rows.length * sizes.rowHeight

  const totalDays = totalDaysOf(range)
  const availableWidth = viewportWidth
    ? Math.floor(viewportWidth - leftPanelWidth)
    : UNMEASURED_CHART_WIDTH
  const effectiveZoom = zoom ?? defaultZoomFor(totalDays, availableWidth)
  const dayWidth =
    print?.dayWidth ??
    resolveDayWidth(effectiveZoom, totalDays, availableWidth, sizes.maxDayWidth)

  const layout = useMemo(
    () =>
      buildGanttLayout({
        scheduled,
        range,
        dependencies,
        dayWidth,
        rowHeight: sizes.rowHeight,
        stub: sizes.stub,
        today
      }),
    [scheduled, range, dependencies, dayWidth, sizes.rowHeight, sizes.stub, today]
  )
  const { bars, barById, todayOffset, chartWidth } = layout
  const todayAlign = todayOffset === null ? "center" : todayBadgeAlign(todayOffset, chartWidth)

  const axisUnit = axisUnitFor(dayWidth)
  const bandUnit = bandUnitFor(axisUnit)
  const ticks = useMemo(() => buildAxisTicks(range, axisUnit), [range, axisUnit])
  const bandTicks = useMemo(
    () => (bandUnit ? buildAxisTicks(range, bandUnit) : []),
    [range, bandUnit]
  )

  // Zooming keeps the date under the pointer (or the middle of the view,
  // for the buttons) where it was: the scroll offset that holds it is
  // applied once the new scale has rendered.
  const dayWidthRef = useRef(dayWidth)
  dayWidthRef.current = dayWidth
  const pendingAnchor = useRef<{ pointerX: number; scrollLeft: number; dayWidth: number } | null>(
    null
  )

  const zoomTo = useCallback(
    (next: GanttZoom, pointerX?: number) => {
      const el = scrollRef.current
      if (el) {
        pendingAnchor.current = {
          pointerX: pointerX ?? Math.max(0, (el.clientWidth - leftPanelWidth) / 2),
          scrollLeft: el.scrollLeft,
          dayWidth: dayWidthRef.current
        }
      }
      onZoomChange(next)
    },
    [leftPanelWidth, onZoomChange]
  )

  useLayoutEffect(() => {
    const anchor = pendingAnchor.current
    const el = scrollRef.current
    if (!anchor || !el) return
    pendingAnchor.current = null
    el.scrollLeft = anchoredScrollLeft({ ...anchor, nextDayWidth: dayWidth })
  }, [dayWidth])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const pointerXOf = (clientX: number) =>
      Math.max(0, clientX - el.getBoundingClientRect().left - leftPanelWidth)

    const onWheel = (e: WheelEvent) => {
      // A trackpad pinch arrives as a wheel with ctrlKey set.
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const lines = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 800 : 1
      zoomTo(wheelZoom(dayWidthRef.current, e.deltaY * lines), pointerXOf(e.clientX))
    }

    // Safari reports a pinch as its own gesture events instead.
    let gestureBase = dayWidthRef.current
    const onGestureStart = (e: Event) => {
      e.preventDefault()
      gestureBase = dayWidthRef.current
    }
    const onGestureChange = (e: Event) => {
      e.preventDefault()
      const gesture = e as Event & { scale?: number; clientX?: number }
      const next = Math.min(Math.max(gestureBase * (gesture.scale ?? 1), MIN_DAY_WIDTH), MAX_DAY_WIDTH)
      zoomTo(next, pointerXOf(gesture.clientX ?? 0))
    }

    el.addEventListener("wheel", onWheel, { passive: false })
    el.addEventListener("gesturestart", onGestureStart)
    el.addEventListener("gesturechange", onGestureChange)
    return () => {
      el.removeEventListener("wheel", onWheel)
      el.removeEventListener("gesturestart", onGestureStart)
      el.removeEventListener("gesturechange", onGestureChange)
    }
  }, [leftPanelWidth, zoomTo])

  // Only the rows around the view are in the page, and only the arrows that
  // cross them; horizontally, only the axis ticks near the view.
  const rowWindow: RowWindow =
    print?.rows ??
    visibleRowWindow({
      scrollTop: scroll.top,
      viewportHeight: viewportHeight ? viewportHeight - headerHeight : 0,
      rowHeight: sizes.rowHeight,
      rowCount: rows.length
    })
  // A printed page shows only its own slice of rows, from the top.
  const bodyOffset = print ? print.rows.first * sizes.rowHeight : 0
  const shownBodyHeight = print
    ? Math.max(print.rows.last - print.rows.first + 1, 0) * sizes.rowHeight
    : bodyHeight
  const inWindow = (rowIndex: number) => rowIndex >= rowWindow.first && rowIndex <= rowWindow.last
  const visibleConnectors = useMemo(
    () => connectorsInRows(layout.connectors, rowWindow),
    [layout.connectors, rowWindow.first, rowWindow.last]
  )
  const tickSpan =
    viewportWidth && !print
    ? {
        from: scroll.left - viewportWidth,
        to: scroll.left + 2 * viewportWidth
      }
    : null
  const tickInView = (tick: { offsetDays: number; widthDays: number }) =>
    !tickSpan ||
    ((tick.offsetDays + tick.widthDays) * dayWidth >= tickSpan.from &&
      tick.offsetDays * dayWidth <= tickSpan.to)

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const next = { top: el.scrollTop, left: el.scrollLeft }
    setScroll((current) => {
      const sameRows =
        Math.floor(current.top / sizes.rowHeight) === Math.floor(next.top / sizes.rowHeight)
      const sameColumns = Math.abs(current.left - next.left) < 64
      return sameRows && sameColumns ? current : next
    })
  }

  const rowTopOf = (rowIndex: number) => rowIndex * sizes.rowHeight
  const barTopOf = (rowIndex: number) =>
    rowTopOf(rowIndex) + (sizes.rowHeight - sizes.barHeight) / 2

  return (
    <div>
      <div className={print ? "hidden" : "flex items-center justify-between gap-3 mb-1.5"}>
        <GanttZoomControl value={effectiveZoom} onChange={(next) => zoomTo(next)} />
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <Switch
            size="small"
            checked={showConnectors}
            onChange={onShowConnectorsChange}
            aria-label={t("milestonesGantt.connectors.toggle")}
          />
          {t("milestonesGantt.connectors.toggle")}
        </label>
        {hasSections && (
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <Switch
              size="small"
              checked={grouped}
              onChange={onGroupedChange}
              aria-label={t("milestonesGantt.grouping.toggle")}
            />
            {t("milestonesGantt.grouping.toggle")}
          </label>
        )}
      </div>
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: "1px solid #eef0f2" }}
        onClick={onClearSelection}
      >
        <div
          ref={scrollRef}
          data-testid="gantt-scroll"
          onScroll={onScroll}
          className={print ? undefined : "overflow-auto"}
          style={
            print ? undefined : { maxHeight: sizes.maxViewportHeight, overscrollBehaviorX: "contain" }
          }
        >
          <div className="flex" style={{ width: leftPanelWidth + chartWidth }}>
            <div
              data-pdf-left={print ? "" : undefined}
              className="flex-shrink-0 bg-white"
              style={{
                width: leftPanelWidth,
                position: "sticky",
                left: 0,
                zIndex: 6,
                borderRight: "1px solid #eef0f2"
              }}
            >
              <div
                className="flex bg-white text-xs font-medium uppercase tracking-wide text-gray-400"
                style={{
                  height: headerHeight,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  borderBottom: "1px solid #eef0f2"
                }}
              >
                <div className="flex items-end px-3 pb-1" style={{ width: sizes.milestoneColWidth }}>
                  Milestone
                </div>
                <div
                  className="flex items-end justify-center px-2 pb-1"
                  style={{ width: sizes.dependenciesColWidth, borderLeft: "1px solid #f5f6f7" }}
                >
                  Dependencies
                </div>
                <div
                  className="flex items-end justify-center px-2 pb-1"
                  style={{ width: sizes.statusColWidth, borderLeft: "1px solid #f5f6f7" }}
                >
                  Status
                </div>
              </div>
              <div className="relative" style={{ height: shownBodyHeight, overflow: "clip" }}>
                {rows.map((row, index) => {
                  if (!inWindow(index)) return null
                  const { milestone } = row
                  const isDimmed = !!connected && !connected.has(milestone.id)
                  const isSelected = selectedId === milestone.id
                  const count = dependencyCountByMilestone.get(milestone.id) || 0
                  return (
                    <div
                      key={milestone.id}
                      data-testid="gantt-row"
                      className="absolute left-0 right-0 flex cursor-pointer transition-colors"
                      style={{
                        top: rowTopOf(index) - bodyOffset,
                        height: sizes.rowHeight,
                        opacity: isDimmed ? 0.4 : 1,
                        borderBottom: "1px solid #f5f6f7",
                        backgroundColor: isSelected ? "#f0f7f6" : "#fff"
                      }}
                      onClick={(e) => onToggleSelect(milestone.id, e)}
                    >
                      <div
                        className="flex items-center gap-2 pr-3 min-w-0"
                        style={{
                          width: sizes.milestoneColWidth,
                          paddingLeft: row.isSection ? 4 : 12 + row.depth * 14
                        }}
                      >
                        {row.isSection && (
                          <button
                            type="button"
                            aria-expanded={!row.isCollapsed}
                            aria-label={t(
                              row.isCollapsed
                                ? "milestonesGantt.grouping.expand"
                                : "milestonesGantt.grouping.collapse",
                              { name: milestone.name }
                            )}
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleSection(milestone.id)
                            }}
                            className="flex items-center justify-center w-5 h-5 flex-shrink-0 rounded text-gray-500 hover:bg-gray-100"
                          >
                            {row.isCollapsed ? (
                              <CaretRightOutlined style={{ fontSize: 10 }} />
                            ) : (
                              <CaretDownOutlined style={{ fontSize: 10 }} />
                            )}
                          </button>
                        )}
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: colorById.get(milestone.id) }}
                        />
                        <Text type="secondary" className="text-xs flex-shrink-0">
                          {row.number}.
                        </Text>
                        <span
                          className={`truncate text-sm${row.isSection ? " font-semibold" : ""}`}
                          title={milestone.name}
                        >
                          {milestone.name}
                        </span>
                        {row.isSection && row.isCollapsed && (
                          <Text type="secondary" className="text-xs flex-shrink-0">
                            ({row.childCount})
                          </Text>
                        )}
                      </div>
                      <div
                        className="flex items-center justify-center"
                        style={{ width: sizes.dependenciesColWidth, borderLeft: "1px solid #f5f6f7" }}
                      >
                        {count ? (
                          <Text style={{ fontSize: 12, color: TEAL }}>
                            <LinkOutlined /> {count}
                          </Text>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            —
                          </Text>
                        )}
                      </div>
                      <div
                        className="flex items-center justify-center"
                        style={{ width: sizes.statusColWidth, borderLeft: "1px solid #f5f6f7" }}
                      >
                        <MilestoneStatusTag
                          status={milestone.status}
                          label={milestone.status_display}
                          style={{ margin: 0, fontSize: 11 }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div
              data-pdf-chart={print ? "" : undefined}
              className="relative bg-white"
              style={{ width: chartWidth }}
            >
              <div
                className="bg-white"
                style={{
                  height: headerHeight,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  borderBottom: "1px solid #eef0f2",
                  overflow: "clip"
                }}
              >
                <div
                  data-testid="gantt-band-row"
                  className="relative"
                  style={{ height: sizes.bandHeaderHeight }}
                >
                  {bandTicks.filter(tickInView).map((tick) => (
                    <div
                      key={`band-${tick.offsetDays}`}
                      className="absolute top-0 h-full flex items-center text-xs font-semibold text-gray-600"
                      style={{
                        left: tick.offsetDays * dayWidth,
                        width: tick.widthDays * dayWidth,
                        borderLeft: tick.offsetDays > 0 ? "1px solid #dfe3e7" : undefined
                      }}
                    >
                      {tick.widthDays * dayWidth >= 40 && (
                        <span
                          className="truncate px-1.5"
                          title={tick.title}
                          style={{ position: "sticky", left: leftPanelWidth }}
                        >
                          {tick.title}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {/* Today gets its own row, entirely separate from the date
                    labels below it — a badge that happened to land over a
                    tick label would make that label unreadable. */}
                <div
                  data-testid="gantt-today-row"
                  className="relative"
                  style={{ height: sizes.todayRowHeight, borderTop: "1px solid #f2f3f4" }}
                >
                  {todayOffset !== null && (
                    <div
                      data-testid="gantt-today-badge"
                      data-align={todayAlign}
                      className={`absolute bottom-0 flex flex-col ${
                        todayAlign === "start"
                          ? "items-start"
                          : todayAlign === "end"
                            ? "items-end"
                            : "items-center"
                      }`}
                      style={{
                        left: todayOffset,
                        transform:
                          todayAlign === "start"
                            ? "translateX(-3px)"
                            : todayAlign === "end"
                              ? "translateX(calc(-100% + 3px))"
                              : "translateX(-50%)",
                        zIndex: 2
                      }}
                    >
                      <span
                        className="text-[10px] font-medium leading-4 px-1.5 rounded whitespace-nowrap"
                        style={{ color: "#fff", backgroundColor: TEAL }}
                      >
                        Today
                      </span>
                      <span
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: "3px solid transparent",
                          borderRight: "3px solid transparent",
                          borderTop: `3px solid ${TEAL}`
                        }}
                      />
                    </div>
                  )}
                </div>
                <div
                  data-testid="gantt-date-row"
                  data-axis-unit={axisUnit}
                  className="relative"
                  style={{ height: sizes.headerHeight, borderTop: "1px solid #f2f3f4" }}
                >
                  {ticks.filter(tickInView).map((tick) => {
                    const label = tickDisplayLabel(tick, axisUnit, dayWidth)
                    return label ? (
                      <div
                        key={tick.offsetDays}
                        data-testid="gantt-axis-tick"
                        title={tick.title}
                        className="absolute top-0 h-full flex items-center text-[11px] text-gray-400 whitespace-nowrap overflow-hidden"
                        style={{
                          left: tick.offsetDays * dayWidth + 4,
                          width: tick.widthDays * dayWidth - 4
                        }}
                      >
                        {label}
                      </div>
                    ) : null
                  })}
                </div>
              </div>

              <div className="relative" style={{ height: shownBodyHeight, overflow: "clip" }}>
                <div
                  className="absolute left-0 right-0"
                  style={{ top: -bodyOffset, height: bodyHeight }}
                >
                {ticks.filter(tickInView).map(
                  (tick) =>
                    tick.offsetDays > 0 && (
                      <div
                        key={`grid-${tick.offsetDays}`}
                        className="absolute top-0 bottom-0"
                        style={{ left: tick.offsetDays * dayWidth, borderLeft: "1px solid #f2f3f4" }}
                      />
                    )
                )}
                {/* A visibly stronger separator at each band boundary — the
                    tick gridlines fade into the background, these don't. */}
                {bandTicks.filter(tickInView).map(
                  (tick) =>
                    tick.offsetDays > 0 && (
                      <div
                        key={`band-sep-${tick.offsetDays}`}
                        className="absolute top-0 bottom-0"
                        style={{ left: tick.offsetDays * dayWidth, borderLeft: "1px solid #dfe3e7" }}
                      />
                    )
                )}

                {todayOffset !== null && (
                  <div
                    className="absolute top-0 bottom-0"
                    style={{ left: todayOffset, borderLeft: `1px dashed ${TEAL}`, opacity: 0.6 }}
                  />
                )}

                <svg
                  data-testid="dependency-connectors"
                  className="absolute top-0 left-0"
                  width={chartWidth}
                  height={bodyHeight}
                  style={{ pointerEvents: "none" }}
                >
                  <defs>
                    <marker
                      id="gantt-dependency-arrow"
                      markerWidth={7}
                      markerHeight={7}
                      refX={5.5}
                      refY={3.5}
                      orient="auto"
                    >
                      <path d="M0,0 L7,3.5 L0,7 Z" fill="#94a3b8" />
                    </marker>
                    <marker
                      id="gantt-dependency-arrow-active"
                      markerWidth={7}
                      markerHeight={7}
                      refX={5.5}
                      refY={3.5}
                      orient="auto"
                    >
                      <path d="M0,0 L7,3.5 L0,7 Z" fill={TEAL} />
                    </marker>
                  </defs>
                  {showConnectors && visibleConnectors.map(({ dep, d }) => {
                    const emphasis = connectorEmphasis(dep, { hoveredId, chain: connected })
                    const isHighlighted = emphasis === "highlight"
                    return (
                      <g
                        key={dep.id}
                        data-dependency={dep.id}
                        data-emphasis={emphasis}
                        opacity={emphasis === "dim" ? 0.15 : 1}
                      >
                        <path
                          d={d}
                          fill="none"
                          stroke={isHighlighted ? TEAL : "#94a3b8"}
                          strokeWidth={isHighlighted ? 2 : 1.5}
                          markerEnd={`url(#gantt-dependency-arrow${isHighlighted ? "-active" : ""})`}
                        />
                      </g>
                    )
                  })}
                </svg>

                {bars.map((bar) => {
                  if (!inWindow(bar.rowIndex)) return null
                  const isSelected = selectedId === bar.milestone.id
                  const isDimmed = !!connected && !connected.has(bar.milestone.id)
                  const barStatus = barStatusFor({
                    status: bar.milestone.status,
                    end: bar.end,
                    today
                  })
                  const isOverdue = barStatus === "overdue"
                  const related = directDependenciesFor(bar.milestone.id)
                  const color = colorById.get(bar.milestone.id) || "#90A4AE"
                  const rawStart = parseDate(bar.milestone.start_date)
                  const rawEnd = parseDate(bar.milestone.end_date)
                  const actualEndDate = parseDate(bar.milestone.actual_end_date)
                  // A one-day bar at most day-widths is nearly invisible as a
                  // rectangle — a small diamond marker reads far more clearly.
                  const isSingleDay = bar.durationDays === 1
                  const rowCentre = rowTopOf(bar.rowIndex) + sizes.rowHeight / 2
                  const labelSide = barLabelSide(bar, chartWidth)
                  const ring = isSelected
                    ? `0 0 0 2px #fff, 0 0 0 4px ${TEAL}`
                    : isOverdue
                      ? `0 0 0 2px ${OVERDUE_RED}`
                      : bar.isShifted
                        ? "0 0 0 1.5px #f59e0b"
                        : "0 1px 2px rgba(16,24,32,0.08)"
                  const dates = isSingleDay
                    ? bar.start.format("MMM D")
                    : formatWeekRange(bar.start, bar.end)
                  const dateLabel = isOverdue
                    ? `${t("milestonesGantt.legend.overdue")} · ${dates}`
                    : dates

                  return (
                    <React.Fragment key={bar.milestone.id}>
                      <Tooltip
                        title={
                          <MilestoneBarTooltipContent
                            name={bar.milestone.name}
                            start={bar.start}
                            end={bar.end}
                            durationDays={bar.durationDays}
                            statusLabel={bar.milestone.status_display}
                            related={related}
                            isShifted={bar.isShifted}
                            shiftReasonName={
                              bar.shiftReason
                                ? nameById.get(bar.shiftReason.sourceMilestoneId) || null
                                : null
                            }
                            rawStart={rawStart}
                            rawEnd={rawEnd}
                            isOverdue={isOverdue}
                          />
                        }
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label={`${bar.milestone.name} · ${bar.start.format("MMM D")} – ${bar.end.format("MMM D")}${bar.isShifted ? " (shifted)" : ""}`}
                          data-bar-status={barStatus}
                          onClick={(e) => onToggleSelect(bar.milestone.id, e)}
                          onMouseEnter={() => setHoveredId(bar.milestone.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          onFocus={() => setHoveredId(bar.milestone.id)}
                          onBlur={() => setHoveredId(null)}
                          className="absolute flex items-center justify-center text-white cursor-pointer overflow-hidden"
                          style={
                            isSingleDay
                              ? {
                                  left: bar.left + bar.width / 2 - 7,
                                  top: rowCentre - 7,
                                  width: 14,
                                  height: 14,
                                  borderRadius: 3,
                                  transform: "rotate(45deg)",
                                  backgroundColor: color,
                                  opacity: isDimmed ? 0.35 : 1,
                                  boxShadow: ring,
                                  zIndex: isSelected ? 3 : 2
                                }
                              : {
                                  left: bar.left,
                                  top: barTopOf(bar.rowIndex),
                                  width: bar.width,
                                  height: sizes.barHeight,
                                  borderRadius: Math.min(6, bar.width / 2),
                                  backgroundColor: color,
                                  opacity: isDimmed ? 0.35 : 1,
                                  boxShadow: ring,
                                  zIndex: isSelected ? 3 : 2
                                }
                          }
                        >
                          {!isSingleDay && barStatus === "completed" && bar.width >= 16 && (
                            <CheckOutlined style={{ fontSize: 11 }} />
                          )}
                          {!isSingleDay && isOverdue && bar.width >= 16 && (
                            <WarningFilled style={{ fontSize: 11 }} />
                          )}
                          {!isSingleDay && labelSide === "inside" && (
                            <span className="text-[11px] whitespace-nowrap ml-1.5">{dateLabel}</span>
                          )}
                        </div>
                      </Tooltip>
                      {labelSide !== "inside" && labelSide !== "hidden" && (
                        <span
                          data-testid="gantt-bar-label"
                          className="absolute text-[11px] whitespace-nowrap flex items-center gap-1"
                          style={{
                            ...(labelSide === "after"
                              ? {
                                  left: (isSingleDay ? bar.left + Math.max(bar.width, 14) : bar.left + bar.width) + 8
                                }
                              : { right: chartWidth - bar.left + 8 }),
                            top: barTopOf(bar.rowIndex),
                            height: sizes.barHeight,
                            lineHeight: `${sizes.barHeight}px`,
                            color: isOverdue ? OVERDUE_RED : bar.isShifted ? "#b45309" : "#8c96a0",
                            opacity: isDimmed ? 0.35 : 1
                          }}
                        >
                          {(bar.isShifted || isOverdue) && <WarningFilled style={{ fontSize: 10 }} />}
                          {dateLabel}
                        </span>
                      )}
                      {bar.actualVsPlanned && (
                        <div
                          aria-label={t(
                            bar.actualVsPlanned.days > 0
                              ? "milestonesGantt.actual.late"
                              : "milestonesGantt.actual.early",
                            { name: bar.milestone.name, count: Math.abs(bar.actualVsPlanned.days) }
                          )}
                          className="absolute"
                          style={{
                            left: bar.actualVsPlanned.left,
                            top: barTopOf(bar.rowIndex),
                            width: bar.actualVsPlanned.width,
                            height: sizes.barHeight,
                            borderRadius: 4,
                            border: `1.5px dashed ${color}`,
                            background:
                              bar.actualVsPlanned.days > 0
                                ? `repeating-linear-gradient(135deg, transparent 0 4px, ${color}33 4px 8px)`
                                : "rgba(255,255,255,0.55)",
                            opacity: isDimmed ? 0.35 : 1,
                            pointerEvents: "none",
                            zIndex: 3
                          }}
                        />
                      )}
                      {/* A separate, subtle marker for when the milestone ACTUALLY
                          finished — never a second bar, and never in place of the
                          planned one, which keeps representing the plan exactly
                          as scheduled regardless of how it played out. */}
                      {bar.actualEndOffset !== null && actualEndDate && (
                        <Tooltip title={`Actually finished ${actualEndDate.format("MMM D")}`}>
                          <div
                            aria-label={`${bar.milestone.name} actually finished ${actualEndDate.format("MMM D")}`}
                            className="absolute rounded-full"
                            style={{
                              left: bar.actualEndOffset - 4,
                              top: barTopOf(bar.rowIndex) + sizes.barHeight - 4,
                              width: 8,
                              height: 8,
                              backgroundColor: "#1f2937",
                              border: "1.5px solid #fff",
                              boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
                              opacity: isDimmed ? 0.35 : 1,
                              zIndex: 4
                            }}
                          />
                        </Tooltip>
                      )}
                    </React.Fragment>
                  )
                })}

                {rows.map(({ milestone }, rowIndex) => {
                  if (!inWindow(rowIndex) || barById.has(milestone.id)) return null
                  const hasPredecessor = dependencies.some(
                    (d) => d.target_milestone === milestone.id
                  )
                  // An Effort with no dates and nothing to follow: listed, not
                  // drawn, since there is no date to put it on.
                  if (isUnscheduledMilestone(milestone, hasPredecessor)) {
                    return (
                      <div
                        key={milestone.id}
                        className="absolute flex items-center"
                        style={{
                          left: 8,
                          top: rowTopOf(rowIndex),
                          height: sizes.rowHeight
                        }}
                      >
                        <Tooltip title={t("projectMilestones.tooltips.unscheduled")}>
                          <Tag bordered={false} className="m-0 text-[11px]">
                            {t("projectMilestones.cells.unscheduled")}
                          </Tag>
                        </Tooltip>
                      </div>
                    )
                  }
                  // No dependency and no bar means nothing to say — the row
                  // stays empty rather than repeating "Dates not set".
                  const hasDependency = !!dependencyCountByMilestone.get(milestone.id)
                  if (!hasDependency) return null
                  return (
                    <div
                      key={milestone.id}
                      className="absolute flex items-center gap-1 text-[11px] whitespace-nowrap"
                      style={{
                        left: 8,
                        top: rowTopOf(rowIndex),
                        height: sizes.rowHeight,
                        color: "#d97706"
                      }}
                    >
                      <WarningFilled style={{ fontSize: 11 }} />
                      <span>Dates required to display timeline position</span>
                    </div>
                  )
                })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MilestonesGanttLegend() {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mt-2 px-1">
      <div className="flex items-center gap-3 flex-wrap">
        {MILESTONE_STATUS_ORDER.map((status) => (
          <ActionTooltip
            key={status}
            title={t(MILESTONE_STATUSES_MAP[status].tooltipKey)}
          >
            <div className="flex items-center gap-1.5" tabIndex={0}>
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: MILESTONE_STATUSES_MAP[status]?.style?.color }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(MILESTONE_STATUSES_MAP[status].labelKey)}
              </Text>
            </div>
          </ActionTooltip>
        ))}
        <div className="flex items-center gap-1.5">
          <WarningFilled style={{ fontSize: 11, color: "#f59e0b" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Shifted by a dependency
          </Text>
        </div>
        <div className="flex items-center gap-1.5">
          <WarningFilled style={{ fontSize: 11, color: OVERDUE_RED }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("milestonesGantt.legend.overdue")}
          </Text>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block"
            style={{ width: 16, height: 8, borderRadius: 2, border: "1.5px dashed #94a3b8" }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("milestonesGantt.legend.actualVsPlanned")}
          </Text>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block"
            style={{ width: 0, height: 12, borderLeft: `1px dashed ${TEAL}` }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("milestonesGantt.legend.today")}
          </Text>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ color: "#94a3b8", fontSize: 13 }}>→</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Dependency
        </Text>
      </div>
    </div>
  )
}

function MilestoneBarTooltipContent({
  name,
  start,
  end,
  durationDays,
  statusLabel,
  related,
  isShifted,
  shiftReasonName,
  rawStart,
  rawEnd,
  isOverdue = false
}: {
  name: string
  start: Dayjs
  end: Dayjs
  durationDays: number
  statusLabel: string
  related: RelatedDependencyRow[]
  isShifted: boolean
  shiftReasonName: string | null
  rawStart: Dayjs | null
  rawEnd: Dayjs | null
  isOverdue?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div style={{ maxWidth: 260 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{name}</div>
      {isOverdue && (
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
          <WarningFilled /> {t("milestonesGantt.state.overdue")}
        </div>
      )}
      <div style={{ fontSize: 12, opacity: 0.85 }}>
        {start.format("MMM D")} – {end.format("MMM D")}
      </div>
      <div style={{ fontSize: 12, opacity: 0.85 }}>
        {durationDays} day{durationDays === 1 ? "" : "s"} · {statusLabel}
      </div>
      {isShifted && rawStart && rawEnd && (
        <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 4 }}>
          Originally planned {rawStart.format("MMM D")} – {rawEnd.format("MMM D")}
          {shiftReasonName ? ` — moved because ${shiftReasonName} finishes later.` : ""}
        </div>
      )}
      {related.length > 0 && (
        <div style={{ marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 6 }}>
          {related.map(({ dependency, otherName, relation }) => (
            <div key={dependency.id} style={{ fontSize: 12, marginBottom: 2 }}>
              {relation}: {otherName}
              <div style={{ opacity: 0.75 }}>
                {TYPE_INFO[dependency.type].label}
                {lagSuffix(t, dependency.lag_days)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MilestoneDetailPanel({
  milestone,
  related,
  effectiveEntry,
  nameById,
  parseDate
}: {
  milestone: ProjectMilestone
  related: RelatedDependencyRow[]
  effectiveEntry: EffectiveScheduleEntry | null
  nameById: Map<string, string>
  parseDate: (value: unknown) => Dayjs | null
}) {
  const { t } = useTranslation()
  const rawStart = parseDate(milestone.start_date)
  const rawEnd = parseDate(milestone.end_date)
  const start = effectiveEntry?.start ?? rawStart
  const end = effectiveEntry?.end ?? rawEnd
  const durationDays = start && end ? end.diff(start, "day") + 1 : null

  return (
    <div
      data-testid="milestone-detail-panel"
      className="mt-2 p-3 rounded-lg"
      style={{ border: "1px solid #eef0f2", background: "#fafbfb" }}
    >
      <MilestoneDetailHeader name={milestone.name} statusLabel={milestone.status_display} />
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs" style={{ color: "#6b7280" }}>
        <span>
          {start && end ? `${start.format("MMM D")} – ${end.format("MMM D")}` : "Dates not set"}
        </span>
        {durationDays !== null && <span>{durationDays} days</span>}
      </div>
      {effectiveEntry?.isShifted && rawStart && rawEnd && (
        <div
          className="mt-2 flex items-start gap-1.5 text-xs px-2 py-1.5 rounded"
          style={{ background: "#fffbeb", color: "#92400e" }}
        >
          <WarningFilled style={{ fontSize: 12, marginTop: 1 }} />
          <span>
            Originally planned {rawStart.format("MMM D")} – {rawEnd.format("MMM D")}. Shifted
            because{" "}
            {effectiveEntry.shiftReason
              ? nameById.get(effectiveEntry.shiftReason.sourceMilestoneId) || "a dependency"
              : "a dependency"}{" "}
            finishes later.
          </span>
        </div>
      )}
      {related.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {related.map(({ dependency, otherName, relation }) => (
            <div key={dependency.id} className="text-xs flex items-center gap-1.5">
              <LinkOutlined style={{ color: TEAL, fontSize: 11 }} />
              <Text style={{ fontSize: 12 }}>
                {relation} <Text strong style={{ fontSize: 12 }}>{otherName}</Text> ·{" "}
                {TYPE_INFO[dependency.type].label}
                {lagSuffix(t, dependency.lag_days)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MilestoneDetailHeader({ name, statusLabel }: { name: string; statusLabel: string }) {
  return (
    <div className="flex items-center gap-2">
      <Text strong style={{ fontSize: 13 }}>
        {name}
      </Text>
      <span
        className="text-[11px] px-1.5 py-0.5 rounded"
        style={{ background: "#eef0f2", color: "#6b7280" }}
      >
        {statusLabel}
      </span>
    </div>
  )
}
